import { useState, useEffect } from 'react'
import { supabaseGetter } from '~/api/supabase-getter'

/**
 * Custom hook for fetching crop detail fields (template, archetype, growth, lifecycle, notes).
 * Reads from crop_details table — only populated for non-succession crops.
 *
 * @param {Object} params
 * @param {string} params.cropId
 * @returns {Object} { cropDetails, loading, error, invalidate }
 */
export const useCropDetails = ({ cropId }) => {
  const [cropDetails, setCropDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    async function fetchCropDetails() {
      if (!cropId) {
        setCropDetails(null)
        setLoading(false)
        setError(null)
        return
      }

      setLoading(true)
      setError(null)

      try {
        // Fetch crop_details first — maybeSingle returns null (not an error) for child crops
        const { data: detailData, error: detailError } = await supabaseGetter.cropDetails.getById(cropId)
        if (detailError) throw detailError
        if (!detailData) {
          // No crop_details row — fall back to crops table directly
          // (happens for crops created before migration or during transitional state)
          const { data: fallbackCropData } = await supabaseGetter.crops.getById(cropId)
          setCropDetails(fallbackCropData || null)
          return
        }

        // Fetch crop-level fields separately (organization_id, count, etc.)
        // Use safe spread — if this query fails, detail fields are still returned
        const { data: cropData } = await supabaseGetter.crops.getById(cropId)

        // Merge: detail fields (archetype_code, template_id, growth_code, …)
        // overridden by crop fields (id, organization_id, count, created_at, …)
        setCropDetails({ ...detailData, ...(cropData || {}) })
      } catch (err) {
        console.error('Error fetching crop details:', err)
        setError(err.message)
        setCropDetails(null)
      } finally {
        setLoading(false)
      }
    }

    fetchCropDetails()
  }, [cropId])

  return {
    cropDetails,
    loading,
    error,
    invalidate: supabaseGetter.cropDetails.invalidate
  }
}
